import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
    FormControl, InputLabel, Chip, Container, TextField,
    Avatar, Paper, IconButton, Divider, InputBase, Autocomplete, Collapse, Grow
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
};

const stringAvatar = (name) => {
    if (!name) return { sx: { bgcolor: 'grey.500' }, children: '?' };
    return {
        sx: {
            bgcolor: stringToColor(name),
            width: 40,
            height: 40,
            fontSize: '1rem',
            fontWeight: 'bold',
        },
        children: `${name.split(' ')[0][0]}${name.split(' ')[1] ? name.split(' ')[1][0] : ''}`,
    };
};

const TeamNode = ({ node, level, onAssignProject }) => {
    const [open, setOpen] = useState(true);
    const hasChildren = node.children && node.children.length > 0;

    return (
        <Box sx={{ position: 'relative', ml: { xs: 0, md: level * 4 }, mb: 1.5 }}>
            {/* Connection Line to Left */}
            {level > 0 && (
                <Box sx={{
                    position: 'absolute',
                    top: 24,
                    left: { xs: -10, md: -20 },
                    width: { xs: 10, md: 20 },
                    height: 2,
                    bgcolor: 'grey.300',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                }} />
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        width: { xs: 250, sm: 280 },
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            borderColor: 'primary.main',
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Box>
                            <Avatar {...stringAvatar(node.name)} />
                        </Box>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" noWrap>{node.name}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">{node.role || 'No Role'}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ px: 2, pb: 2 }}>
                        <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                                Current Projects
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {node.current_projects && node.current_projects[0] !== 'None' ? (
                                    node.current_projects.map((proj, i) => (
                                        <Chip
                                            key={i}
                                            label={proj}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                        />
                                    ))
                                ) : (
                                    <Chip label="None" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', color: 'text.disabled', borderColor: 'divider' }} />
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        disableElevation
                        onClick={() => onAssignProject(node)}
                        sx={{
                            borderRadius: 0,
                            py: 1,
                            bgcolor: level === 0 ? 'primary.main' : 'primary.light',
                            '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        ASSIGN PROJECT
                    </Button>
                </Paper>

                {/* Vertical Toggle for Children */}
                {hasChildren && (
                    <IconButton
                        size="small"
                        onClick={() => setOpen(!open)}
                        sx={{ mt: 1, ml: 1, bgcolor: 'grey.100' }}
                    >
                        {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>
                )}
            </Box>

            {/* Vertical Connectivity Line */}
            <Collapse in={open}>
                {hasChildren && (
                    <Box sx={{ position: 'relative', mt: 1 }}>
                        <Box sx={{
                            position: 'absolute',
                            top: -24,
                            left: 20,
                            bottom: 20,
                            width: 2,
                            bgcolor: 'grey.300',
                            display: { xs: 'none', md: 'block' }
                        }} />

                        {/* Actually, let's just use strict left border indentation */}
                        <Box sx={{ borderLeft: { xs: '2px solid #e0e0e0', md: 'none' }, ml: { xs: 1, md: 2 }, pl: { xs: 1, md: 2 }, pt: 1 }}>
                            {node.children.map(child => (
                                <TeamNode
                                    key={child.id}
                                    node={child}
                                    level={level + 1}
                                    onAssignProject={onAssignProject}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Collapse>
        </Box>
    );
};

function Team() {
    const [team, setTeam] = useState([]);
    const [projects, setProjects] = useState([]);
    const [roles, setRoles] = useState([]);
    const [countries, setCountries] = useState([]);
    const [divisions, setDivisions] = useState([]);

    const [loading, setLoading] = useState(true);
    const [openAssign, setOpenAssign] = useState(false);
    const [openAddUser, setOpenAddUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [assignment, setAssignment] = useState({ project_id: '', role: '' });
    const [newUser, setNewUser] = useState({
        full_name: '', username: '', email: '', password: '',
        role_id: '', country_ids: [], division_ids: [], manager_id: ''
    });
    const [searchTerm, setSearchTerm] = useState('');

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [teamRes, projRes, rolesRes, countriesRes, divisionsRes] = await Promise.all([
                axios.get(`${API_URL}/team`, { headers }),
                axios.get(`${API_URL}/projects`, { headers }),
                axios.get(`${API_URL}/setup/roles`, { headers }),
                axios.get(`${API_URL}/setup/countries`, { headers }),
                axios.get(`${API_URL}/setup/divisions`, { headers })
            ]);
            setTeam(teamRes.data);
            setProjects(projRes.data);
            setRoles(rolesRes.data);
            setCountries(countriesRes.data);
            setDivisions(divisionsRes.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAssignProject = async () => {
        try {
            await axios.post(`${API_URL}/team/assign`, { user_id: selectedUser.id, ...assignment }, { headers });
            setOpenAssign(false);
            fetchData();
        } catch (err) {
            alert('Error assigning project');
        }
    };

    const handleAddUser = async () => {
        try {
            await axios.post(`${API_URL}/setup/users`, newUser, { headers });
            setOpenAddUser(false);
            setNewUser({ full_name: '', username: '', email: '', password: '', role_id: '', country_ids: [], division_ids: [], manager_id: '' });
            fetchData();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Error creating user');
        }
    };

    const openAssignDialog = (user) => {
        setSelectedUser(user);
        setAssignment({ project_id: '', role: '' });
        setOpenAssign(true);
    }

    // Build Tree (Generic) - but applied per division
    const buildTree = (users) => {
        if (!users) return [];

        // Create map
        const userMap = {};
        users.forEach(u => {
            userMap[u.id] = { ...u, children: [] };
        });

        const roots = [];
        users.forEach(u => {
            const node = userMap[u.id];
            // Only if manager is ALSO in this list do we nest it. 
            // Otherwise treat as root (division head)
            if (u.manager_id && userMap[u.manager_id]) {
                userMap[u.manager_id].children.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    };

    if (loading) return <Loading message="Loading Team..." />;

    // Filter Logic
    const filteredTeam = team.filter(user =>
        (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.role && user.role.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Group by Country then Division (M2M aware)
    const groupedTeam = filteredTeam.reduce((acc, user) => {
        const userCountries = (user.countries && user.countries.length > 0) ? user.countries : ['Global'];
        const userDivisions = (user.divisions && user.divisions.length > 0) ? user.divisions : ['General'];

        userCountries.forEach(country => {
            if (!acc[country]) acc[country] = {};
            userDivisions.forEach(division => {
                if (!acc[country][division]) acc[country][division] = [];
                acc[country][division].push(user);
            });
        });

        return acc;
    }, {});

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header Section */}
            <Grow in={true} timeout={500}>
                <Box sx={{ 
                    mb: 5, 
                    p: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background Decorative Circles */}
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3 }}>
                        <Box>
                            <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: -1, color: 'white', fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}>
                                Team Directory
                            </Typography>
                            <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Organization by Country and Division
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Paper elevation={0} sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', minWidth: { sm: 250 }, border: 'none', borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', color: 'white' }}>
                                <IconButton sx={{ p: '10px', color: 'white' }} aria-label="search">
                                    <SearchIcon />
                                </IconButton>
                                <InputBase
                                    sx={{ ml: 1, flex: 1, color: 'white', '& input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }}
                                    placeholder="Search employee..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Paper>
                            <Button
                                variant="contained"
                                startIcon={<PersonAddIcon />}
                                onClick={() => setOpenAddUser(true)}
                                sx={{ whiteSpace: 'nowrap', borderRadius: 3, bgcolor: 'white', color: '#1e3c72', fontWeight: 'bold', boxShadow: '0 4px 14px rgba(0,0,0,0.1)', '&:hover': { bgcolor: '#f0f0f0' } }}
                            >
                                Add Member
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Grow>

            <Grow in={true} timeout={800}>
            <Box sx={{ minHeight: 400 }}>
                {filteredTeam.length === 0 && (
                    <Typography align="center" color="text.secondary" sx={{ py: 4 }}>No team members found.</Typography>
                )}

                {Object.entries(groupedTeam).map(([country, divisions]) => (
                    <Box key={country} sx={{ mb: 5 }}>
                        <Typography variant="h5" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                            {country}
                        </Typography>
                        <Divider sx={{ mb: 3, borderColor: 'primary.main', borderWidth: 1 }} />

                        {Object.entries(divisions).map(([division, users]) => {
                            // Build hierarchy for this division
                            const divisionRoots = buildTree(users);

                            return (
                                <Box key={division} sx={{ mb: 4, pl: { md: 2 } }}>
                                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main' }} />
                                        {division}
                                    </Typography>

                                    {/* Tree View Container */}
                                    <Box sx={{ overflowX: 'auto', pb: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {divisionRoots.map(rootNode => (
                                                <TeamNode
                                                    key={rootNode.id}
                                                    node={rootNode}
                                                    level={0}
                                                    onAssignProject={openAssignDialog}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
                ))}
            </Box>
            </Grow>

            {/* Assign Project Dialog */}
            <Dialog open={openAssign} onClose={() => setOpenAssign(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>Assign Project</DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                        Assigning <b>{selectedUser?.name}</b> to:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={projects}
                            getOptionLabel={(option) => option.name || ''}
                            value={projects.find(p => p.id === assignment.project_id) || null}
                            onChange={(event, newValue) => {
                                setAssignment({
                                    ...assignment,
                                    project_id: newValue ? newValue.id : ''
                                });
                            }}
                            renderInput={(params) => <TextField {...params} label="Project" />}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select value={assignment.role} label="Role" onChange={e => setAssignment({ ...assignment, role: e.target.value })}>
                                <MenuItem value="Lead">Lead</MenuItem>
                                <MenuItem value="Member">Member</MenuItem>
                                <MenuItem value="Reviewer">Reviewer</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenAssign(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleAssignProject} variant="contained" disableElevation>Assign</Button>
                </DialogActions>
            </Dialog>

            {/* Add User Dialog */}
            <Dialog open={openAddUser} onClose={() => setOpenAddUser(false)} maxWidth="md" fullWidth>
                <DialogTitle>Add New Team Member</DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Full Name" value={newUser.full_name} onChange={e => setNewUser({ ...newUser, full_name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Username" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Email" type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Password" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select value={newUser.role_id} label="Role" onChange={e => setNewUser({ ...newUser, role_id: e.target.value })}>
                                    {roles.filter(r => !['Country Manager', 'Division Manager'].includes(r.name)).map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                multiple
                                fullWidth
                                options={countries}
                                getOptionLabel={(option) => option.name}
                                value={countries.filter(c => newUser.country_ids.includes(c.id))}
                                onChange={(event, newValue) => {
                                    setNewUser({ ...newUser, country_ids: newValue.map(c => c.id) });
                                }}
                                renderInput={(params) => <TextField {...params} label="Countries" placeholder="Select countries" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                multiple
                                fullWidth
                                options={divisions}
                                getOptionLabel={(option) => option.name}
                                value={divisions.filter(d => newUser.division_ids.includes(d.id))}
                                onChange={(event, newValue) => {
                                    setNewUser({ ...newUser, division_ids: newValue.map(d => d.id) });
                                }}
                                renderInput={(params) => <TextField {...params} label="Divisions" placeholder="Select divisions" />}
                            />
                        </Grid>

                        {/* Manager Selection */}
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={team}
                                getOptionLabel={(option) => {
                                    if (!option.name) return '';
                                    const divStr = (option.divisions && option.divisions.length > 0) ? option.divisions.join(', ') : 'General';
                                    return `${option.name} (${option.role} - ${divStr})`;
                                }}
                                value={team.find(u => u.id === newUser.manager_id) || null}
                                onChange={(event, newValue) => {
                                    setNewUser({ ...newUser, manager_id: newValue ? newValue.id : '' });
                                }}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                        <Box display="flex" flexDirection="column">
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {option.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.role} • {(option.divisions && option.divisions.length > 0) ? option.divisions.join(', ') : 'General'} • {(option.countries && option.countries.length > 0) ? option.countries.join(', ') : 'Global'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                renderInput={(params) => <TextField {...params} label="Manager" placeholder="Search manager..." />}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddUser(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddUser}>Create User</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default Team;
